import React, { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Checkbox } from "../../components/ui/checkbox";
import { Users, User } from "lucide-react";

const AVATAR_COLORS = ["var(--tga-blue)", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#06B6D4", "#84CC16"];

function hashColor(name) {
  let h = 0;
  for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

export function MitarbeiterCell({ project, empOptions, allEmpLookup = {}, onToggle, canEdit }) {
  const [open, setOpen] = useState(false);
  const assigned = project.assigned_users || [];
  const backendNames = project.assigned_user_names || {};

  const resolveName = (uid) => {
    return allEmpLookup[uid] || empOptions.find(e => e.value === uid)?.label || backendNames[uid] || null;
  };

  const getInitials = (uid) => {
    const name = resolveName(uid);
    if (name) return name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();
    return null;
  };

  const renderAvatars = () => {
    if (assigned.length === 0) {
      return canEdit
        ? <Users className="w-3.5 h-3.5" style={{ color: "var(--tga-text-dim)" }} />
        : <span style={{ color: "var(--tga-text-dim)" }}>{"\u2014"}</span>;
    }
    return (
      <div className="flex -space-x-1.5">
        {assigned.slice(0, 3).map(uid => {
          const initials = getInitials(uid);
          const empName = resolveName(uid);
          const bg = empName ? hashColor(empName) : "var(--tga-border-strong)";
          return (
            <div
              key={uid}
              className="w-5 h-5 rounded-full text-[9px] font-semibold flex items-center justify-center transition-transform duration-150 hover:scale-110 hover:z-10"
              style={{
                backgroundColor: bg,
                color: "#fff",
                boxShadow: "0 0 0 1.5px var(--tga-bg-content)",
              }}
              title={empName || "Unbekannt"}
            >
              {initials || <User className="w-2.5 h-2.5" />}
            </div>
          );
        })}
        {assigned.length > 3 && (
          <div
            className="w-5 h-5 rounded-full text-[9px] font-semibold flex items-center justify-center"
            style={{
              backgroundColor: "var(--tga-border-strong)",
              color: "var(--tga-text-secondary)",
              boxShadow: "0 0 0 1.5px var(--tga-bg-content)",
            }}
          >
            +{assigned.length - 3}
          </div>
        )}
      </div>
    );
  };

  if (!canEdit) return <div className="flex items-center gap-1">{renderAvatars()}</div>;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          className="flex items-center gap-1 cursor-pointer min-h-[24px] rounded-sm border transition-colors duration-150"
          style={{
            borderColor: "transparent",
            background: "transparent",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--tga-border-strong)"; e.currentTarget.style.background = "var(--tga-bg-card)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = "transparent"; e.currentTarget.style.background = "transparent"; }}
          data-testid={`mitarbeiter-cell-${project.id}`}
        >
          {renderAvatars()}
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="w-56 p-2"
        align="start"
        style={{
          background: "var(--tga-bg-card)",
          border: "1px solid var(--tga-border-strong)",
          borderRadius: "var(--tga-radius-md)",
          boxShadow: "var(--tga-shadow-lg)",
        }}
      >
        <div className="text-[11px] font-semibold mb-1.5 px-1" style={{ color: "var(--tga-text-muted)" }}>
          Mitarbeiter zuweisen
        </div>
        <div className="max-h-48 overflow-y-auto space-y-0.5">
          {empOptions.map(emp => (
            <label
              key={emp.value}
              className="flex items-center gap-2 px-1.5 py-1 rounded-sm cursor-pointer text-[12px] transition-colors duration-100"
              style={{ color: "var(--tga-text-primary)" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--tga-bg-hover)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              data-testid={`assign-user-${emp.value}`}
            >
              <Checkbox
                checked={assigned.includes(emp.value)}
                onCheckedChange={() => onToggle(project.id, emp.value, assigned)}
              />
              {emp.label}
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
